import { Button, Checkbox, Dialog, DialogActions, DialogContent, Stack } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
    DialogTitleWithClose,
    IOrganisation,
    IParams,
    SortOrder,
    Table,
    useDebouncedState,
    useGetOrganisations,
    useQueryParams,
} from '../../../shared';
import { SearchInput } from '../../../shared/components/search/search.component';
import { useGetSupportCase, useSaveSupportCase } from '../../hooks';

interface Props {
    open: boolean;
    onClose: () => void;
}

export const EditOrganisationAccessDialog: FC<Props> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [allowedOrganisations, setAllowedOrganisations] = useState<{ id: string }[]>([]);
    const { id } = useParams<keyof IParams>() as IParams;
    const { data: supportCase, dataUpdatedAt } = useGetSupportCase(id);
    const { page, pageSize, setPage, setPageSize, sort, setSort } = useQueryParams({
        defaultSort: [{ field: 'name', sort: 'asc' }],
    });
    const [debouncedSearch, search, setSearch] = useDebouncedState('');

    const { isLoading, data: organisations } = useGetOrganisations({
        page,
        pageSize,
        sortBy: sort[0].field,
        sortOrder: sort[0].sort?.toUpperCase() as SortOrder,
        search: debouncedSearch,
    });

    const { mutate } = useSaveSupportCase();

    const columns: GridColDef[] = [
        {
            field: 'allowed_organisation',
            headerName: ' ',
            renderCell: ({ row: { id } }: GridCellParams<IOrganisation>) => (
                <Checkbox checked={allowedOrganisations.some((org) => org.id == id)} />
            ),
            sortable: false,
            width: 70,
        },
        { field: 'name', headerName: t('nameLabel'), minWidth: 150, flex: 1 },
    ];

    const editOrganisationPermission = (id: string) => {
        if (supportCase && id) {
            if (!allowedOrganisations.find((org) => org.id == id)) {
                setAllowedOrganisations([...allowedOrganisations, { id: id }]);
            } else {
                setAllowedOrganisations(allowedOrganisations.filter((org) => org.id != id));
            }
        }
    };

    const saveOrganisationPermissions = () => {
        if (supportCase) {
            supportCase.organisations = allowedOrganisations;
            mutate(supportCase);
            onClose();
        }
    };

    const handleClose = () => {
        onClose();
        setAllowedOrganisations(supportCase?.organisations ? supportCase.organisations : []);
    };

    useEffect(() => {
        if (supportCase) {
            setAllowedOrganisations(supportCase.organisations);
        }
    }, [supportCase, dataUpdatedAt]);

    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg" id="edit-permissions-dialog">
                <DialogTitleWithClose onClose={handleClose}>
                    {t(
                        window.carity.environment.shareSupportCases
                            ? 'organisationsWithOwnership'
                            : 'editOrganisationPermissions',
                    )}
                </DialogTitleWithClose>
                <DialogContent>
                    <Stack spacing={3}>
                        <SearchInput value={search} setValue={setSearch} sx={{ mt: 1 }} />

                        <Table
                            rows={organisations?.data || []}
                            columns={columns}
                            pageSizeOptions={[10]}
                            rowCount={organisations?.pagination?.size || 0}
                            page={page}
                            pageSize={pageSize}
                            setPage={setPage}
                            setPageSize={setPageSize}
                            loading={isLoading}
                            sortModel={sort}
                            onSortModelChange={setSort}
                            onRowClick={({ row }) => editOrganisationPermission(row.id || '')}
                            hideFooterSelectedRowCount
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={allowedOrganisations.length === 0}
                        onClick={saveOrganisationPermissions}
                        variant="contained"
                        color="primary"
                        data-testid="save"
                    >
                        {t('save')}
                    </Button>
                    <Button onClick={handleClose} color="primary">
                        {t('cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
